import { useState, useEffect } from 'react';

export const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

const isAppleDevice = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();

  // Check for iOS devices
  const isIOS = /iphone|ipad|ipod/.test(userAgent);

  // Check for macOS
  const isMac = /macintosh|mac os x/.test(userAgent);

  // Check for Safari browser
  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);

  // Check for iPad that identifies as MacOS in newer versions
  const isIPadOS = isMac && navigator.maxTouchPoints > 0;

  return isIOS || isMac || isIPadOS || isSafari;
};

export const isMobileDevice = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isTouch = isTouchDevice();

  // Check for mobile user agents
  const isMobileUA = /iphone|ipod|android|blackberry|windows phone/g.test(userAgent);

  // Check for tablets specifically
  const isTablet = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/g.test(userAgent);

  // Check for small screens
  const maxWidth = window.matchMedia('(max-width: 768px)').matches;

  // Check if it's an Apple device (includes iPad detection)
  const isApple = isAppleDevice();

  // Return true if:
  // 1. It's a touch device AND
  // 2. Either:
  //    - It's a mobile phone (mobile UA)
  //    - It's a tablet (tablet UA or iPad)
  //    - It's a small touch screen device
  return isTouch && (isMobileUA || isTablet || isApple || maxWidth);
};

// Helper function to sanitize URLs
export const sanitizeUrl = (url: string, platformId: string): string => {
  // Special case for email
  if (platformId === 'email') {
    if (url.startsWith('mailto:')) return url;
    return url.includes('@') ? `mailto:${url}` : url;
  }

  // Remove any leading/trailing whitespace
  let sanitized = url.trim();

  // If the URL doesn't have a protocol, add https://
  if (!sanitized.match(/^[a-zA-Z]+:\/\//)) {
    sanitized = `https://${sanitized}`;
  }

  // Ensure URLs don't have double slashes (except after protocol)
  sanitized = sanitized.replace(/([^:])\/\//g, '$1/');

  return sanitized;
};

/**
 * Uses multiple signals:
 * - Touch capability (maxTouchPoints or ontouchstart)
 * - Primary pointer type (coarse = finger/touch, fine = mouse/trackpad)
 * - Hover capability (touch devices typically can't hover)
 */
export const isPrimaryInputTouch = (): boolean => {
  if (typeof window === 'undefined') return false;

  // Check if device has touch capability
  const hasTouch = isTouchDevice();

  // Check if primary pointer is coarse (touch) vs fine (mouse/trackpad)
  // This is the key differentiator - iPads in desktop mode still report coarse pointer
  const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;

  // Check if device can hover (mice can, touch screens can't)
  const cannotHover = window.matchMedia('(hover: none)').matches;

  return hasTouch && (hasCoarsePointer || cannotHover);
};

/**
 * Hook that determines if mobile gamepad should be shown.
 * Detects immediately on load whether the device uses touch as primary input.
 */
export const useShouldShowMobileGamepad = (): boolean => {
  const [shouldShow, setShouldShow] = useState(() => isPrimaryInputTouch());

  useEffect(() => {
    setShouldShow(isPrimaryInputTouch());
  }, []);

  return shouldShow;
};
